/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { ChangeEvent, useState, useEffect } from 'react';
import { Phone, Navigation } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { CallerIdInput } from '../../components/ui/CallerIdInput';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import SelectVIPList from '../../components/ui/SelectVIPList';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { FormState, Strategy } from './types';
import { STRATEGY_OPTIONS } from './constants';

interface QueueProfile {
  id: number;
  name: string;
  enabled: boolean;
}

interface CallbackProfile {
  id: number;
  name: string;
  enabled: boolean;
}

interface VIPList {
  id: number;
  name: string;
  enabled: boolean;
}

interface QueueGeneralSectionProps {
  formData: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
  onStrategyChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onTimeBaseScoreChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

export function QueueGeneralSection({
  formData,
  onFieldChange,
  onStrategyChange,
  onTimeBaseScoreChange
}: QueueGeneralSectionProps) {
  const { t } = useI18n();
  const [availableQueueProfiles, setAvailableQueueProfiles] = useState<QueueProfile[]>([]);
  const [loadingQueueProfiles, setLoadingQueueProfiles] = useState(false);
  const [availableCallbackProfiles, setAvailableCallbackProfiles] = useState<CallbackProfile[]>([]);
  const [loadingCallbackProfiles, setLoadingCallbackProfiles] = useState(false);
  const [availableVIPLists, setAvailableVIPLists] = useState<VIPList[]>([]);
  const [loadingVIPLists, setLoadingVIPLists] = useState(false);

  useEffect(() => {
    const loadQueueProfiles = async () => {
      setLoadingQueueProfiles(true);
      try {
        const response = await fetch(buildApiUrl('/api/telephony/queue-profiles?limit=100&enabled=true'), {
          headers: getApiHeaders()
        });

        if (response.ok) {
          const result = await response.json();
          if (result?.success && Array.isArray(result.data)) {
            setAvailableQueueProfiles(result.data.filter((p: QueueProfile) => p.enabled));
          }
        }
      } catch (error) {
        console.error('Failed to load queue profiles:', error);
      } finally {
        setLoadingQueueProfiles(false);
      }
    };

    loadQueueProfiles();
  }, []);

  useEffect(() => {
    const loadCallbackProfiles = async () => {
      setLoadingCallbackProfiles(true);
      try {
        const response = await fetch(buildApiUrl('/api/telephony/callback-profiles?limit=100&enabled=true'), {
          headers: getApiHeaders()
        });

        if (response.ok) {
          const result = await response.json();
          if (result?.success && Array.isArray(result.data)) {
            setAvailableCallbackProfiles(result.data.filter((p: CallbackProfile) => p.enabled));
          }
        }
      } catch (error) {
        console.error('Failed to load callback profiles:', error);
      } finally {
        setLoadingCallbackProfiles(false);
      }
    };

    loadCallbackProfiles();
  }, []);

  useEffect(() => {
    const loadVIPLists = async () => {
      setLoadingVIPLists(true);
      try {
        const response = await fetch(buildApiUrl('/api/telephony/vip-lists?limit=100&enabled=true'), {
          headers: getApiHeaders()
        });

        if (response.ok) {
          const result = await response.json();
          if (result?.success && Array.isArray(result.data)) {
            setAvailableVIPLists(result.data.filter((p: VIPList) => p.enabled));
          }
        }
      } catch (error) {
        console.error('Failed to load VIP lists:', error);
      } finally {
        setLoadingVIPLists(false);
      }
    };

    loadVIPLists();
  }, []);

  return (
    <>
      <FormBox title={t('queues.form.sections.general')} icon={<Phone className="h-5 w-5" />}>
        <div className="grid grid-cols-12 gap-3">
          {/* Queue Name */}
          <SimpleTooltip content={t('queues.form.fields.queueName.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="queueName" className="text-left">
              {t('queues.form.fields.queueName.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Input
              id="queueName"
              value={formData.queueName}
              onChange={(event) => {
                const newName = event.target.value;
                const newSlug = newName
                  .toLowerCase()
                  .replace(/\s+/g, '_')
                  .replace(/[^a-z0-9_\-]/g, '')
                  .replace(/_+/g, '_')
                  .replace(/^_|_$/g, '')
                  .substring(0, 64);
                onFieldChange('queueName', newName);
                onFieldChange('queueSlug', newSlug);
              }}
              placeholder={t('queues.form.fields.queueName.placeholder')}
              required
            />
          </div>

          {/* Extension */}
          <SimpleTooltip content={t('queues.form.fields.extension.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="extension" className="text-left">
              {t('queues.form.fields.extension.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Input
              id="extension"
              value={formData.extension}
              onChange={(event) => onFieldChange('extension', event.target.value)}
              placeholder={t('queues.form.fields.extension.placeholder')}
            />
          </div>

          {/* Strategy */}
          <SimpleTooltip content={t('queues.form.fields.strategy.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="strategy" className="text-left">
              {t('queues.form.fields.strategy.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <select
              id="strategy"
              value={formData.strategy}
              onChange={onStrategyChange}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {STRATEGY_OPTIONS.map((strategy) => (
                <option key={strategy} value={strategy}>
                  {t(`queues.form.strategies.${strategy}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Caller ID Prefix */}
          <SimpleTooltip content={t('queues.form.fields.callerIdPrefix.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="callerIdPrefix" className="text-left">
              {t('queues.form.fields.callerIdPrefix.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <CallerIdInput
              id="callerIdPrefix"
              nameValue={formData.callerIdNamePrefix || ''}
              numberValue={formData.callerIdNumberPrefix || ''}
              onNameChange={(value) => onFieldChange('callerIdNamePrefix', value)}
              onNumberChange={(value) => onFieldChange('callerIdNumberPrefix', value)}
              namePlaceholder={t('queues.form.fields.callerIdPrefix.namePlaceholder')}
              numberPlaceholder={t('queues.form.fields.callerIdPrefix.numberPlaceholder')}
              nameTooltip={t('queues.form.fields.callerIdPrefix.nameTooltip')}
              numberTooltip={t('queues.form.fields.callerIdPrefix.numberTooltip')}
            />
          </div>

          {/* Queue Profile */}
          <SimpleTooltip content={t('queues.form.fields.queueProfileId.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="queueProfileId" className="text-left">
              {t('queues.form.fields.queueProfileId.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            {loadingQueueProfiles ? (
              <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
            ) : (
              <select
                id="queueProfileId"
                value={formData.queueProfileId}
                onChange={(e) => onFieldChange('queueProfileId', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">{t('queues.form.fields.queueProfileId.none')}</option>
                {availableQueueProfiles.map((profile) => (
                  <option key={profile.id} value={String(profile.id)}>
                    {profile.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Callback Profile */}
          <SimpleTooltip content={t('queues.form.fields.callbackProfileId.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="callbackProfileId" className="text-left">
              {t('queues.form.fields.callbackProfileId.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            {loadingCallbackProfiles ? (
              <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
            ) : (
              <select
                id="callbackProfileId"
                value={formData.callbackProfileId}
                onChange={(e) => onFieldChange('callbackProfileId', e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">{t('queues.form.fields.callbackProfileId.none')}</option>
                {availableCallbackProfiles.map((profile) => (
                  <option key={profile.id} value={String(profile.id)}>
                    {profile.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* VIP List */}
          <SimpleTooltip content={t('queues.form.fields.vipListIds.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="vipListId" className="text-left">
              {t('queues.form.fields.vipListIds.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            {loadingVIPLists ? (
              <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
            ) : (
              <SelectVIPList
                value={formData.vipListIds.length > 0 ? formData.vipListIds[0] : null}
                onChange={(value) => {
                  onFieldChange('vipListIds', value !== null ? [value] : []);
                }}
                vipLists={availableVIPLists}
                placeholder={t('queues.form.fields.vipListIds.none')}
              />
            )}
          </div>

          {/* Max Wait Time */}
          <SimpleTooltip content={t('queues.form.fields.maxWaitTime.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="maxWaitTime" className="text-left">
              {t('queues.form.fields.maxWaitTime.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Input
              id="maxWaitTime"
              type="number"
              min="0"
              value={formData.maxWaitTime}
              onChange={(event) => onFieldChange('maxWaitTime', event.target.value)}
              placeholder={t('queues.form.fields.maxWaitTime.placeholder')}
            />
          </div>

          {/* Wrap-up Time */}
          <SimpleTooltip content={t('queues.form.fields.wrapUpTime.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="wrapUpTime" className="text-left">
              {t('queues.form.fields.wrapUpTime.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Input
              id="wrapUpTime"
              type="number"
              min="0"
              value={formData.wrapUpTime}
              onChange={(event) => onFieldChange('wrapUpTime', event.target.value)}
              placeholder={t('queues.form.fields.wrapUpTime.placeholder')}
            />
          </div>

          {/* Queue Priority */}
          <SimpleTooltip content={t('queues.form.fields.queuePriority.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="queuePriority" className="text-left">
              {t('queues.form.fields.queuePriority.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Input
              id="queuePriority"
              type="number"
              min="0"
              max="10"
              value={formData.queuePriority}
              onChange={(event) => onFieldChange('queuePriority', event.target.value)}
              placeholder={t('queues.form.fields.queuePriority.placeholder')}
            />
          </div>

          {/* Service Level */}
          <SimpleTooltip content={t('queues.form.fields.serviceLevelSeconds.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
            <Label htmlFor="serviceLevelSeconds" className="text-left">
              {t('queues.form.fields.serviceLevelSeconds.label')}
            </Label>
          </SimpleTooltip>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Input
              id="serviceLevelSeconds"
              type="number"
              min="0"
              value={formData.serviceLevelSeconds}
              onChange={(event) => onFieldChange('serviceLevelSeconds', event.target.value)}
              placeholder={t('queues.form.fields.serviceLevelSeconds.placeholder')}
            />
          </div>

          {/* Record */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <SimpleTooltip content={t('queues.form.fields.recordCalls.tooltip')}>
              <Label htmlFor="recordCalls" className="text-left">
                {t('queues.form.fields.recordCalls.label')}
              </Label>
            </SimpleTooltip>
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Toggle
              id="recordCalls"
              value={formData.recordCalls}
              onChange={(value) => onFieldChange('recordCalls', value)}
            />
          </div>

          {/* Announce Hold Time */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <SimpleTooltip content={t('queues.form.fields.announceHoldTime.tooltip')}>
              <Label htmlFor="announceHoldTime" className="text-left">
                {t('queues.form.fields.announceHoldTime.label')}
              </Label>
            </SimpleTooltip>
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Toggle
              id="announceHoldTime"
              value={formData.announceHoldTime}
              onChange={(value) => onFieldChange('announceHoldTime', value)}
            />
          </div>

          {/* Announce Position */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <SimpleTooltip content={t('queues.form.fields.announcePosition.tooltip')}>
              <Label htmlFor="announcePosition" className="text-left">
                {t('queues.form.fields.announcePosition.label')}
              </Label>
            </SimpleTooltip>
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Toggle
              id="announcePosition"
              value={formData.announcePosition}
              onChange={(value) => onFieldChange('announcePosition', value)}
            />
          </div>

          {/* Enabled */}
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <SimpleTooltip content={t('queues.form.fields.enabled.tooltip')}>
              <Label htmlFor="enabled" className="text-left">
                {t('queues.form.fields.enabled.label')}
              </Label>
            </SimpleTooltip>
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <Toggle
              id="enabled"
              value={formData.enabled}
              onChange={(value) => onFieldChange('enabled', value)}
            />
          </div>
        </div>
      </FormBox>

      {/* Destinations Section */}
      <FormBox title={t('queues.form.sections.destinations')} icon={<Navigation className="h-5 w-5" />} className="mt-6">
        <div className="grid grid-cols-12 gap-3">
          {/* After Agent Hangup Destination */}
          <Label className="col-span-12 md:col-span-6 lg:col-span-3">
            <SimpleTooltip content={t('queues.form.fields.afterAgentHangupDestination.tooltip')}>
              <span>{t('queues.form.fields.afterAgentHangupDestination.label')}</span>
            </SimpleTooltip>
          </Label>
          <div className="col-span-12 md:col-span-6 lg:col-span-9">
            <ModuleDestinationSelect
              moduleValue={formData.afterAgentHangupDestinationModule}
              destinationValue={formData.afterAgentHangupDestinationDestination}
              onModuleChange={(value) => onFieldChange('afterAgentHangupDestinationModule', value || '')}
              onDestinationChange={(value) => onFieldChange('afterAgentHangupDestinationDestination', value || '')}
              modulePlaceholder={t('queues.form.fields.afterAgentHangupDestination.modulePlaceholder')}
              destinationPlaceholder={t('queues.form.fields.afterAgentHangupDestination.destinationPlaceholder')}
            />
          </div>

          {/* Final Destination */}
          <Label className="col-span-12 md:col-span-6 lg:col-span-3">
            <SimpleTooltip content={t('queues.form.fields.timeoutDestination.tooltip')}>
              <span>{t('queues.form.fields.timeoutDestination.label')}</span>
            </SimpleTooltip>
          </Label>
          <div className="col-span-12 md:col-span-6 lg:col-span-9">
            <ModuleDestinationSelect
              moduleValue={formData.timeoutDestinationModule}
              destinationValue={formData.timeoutDestinationDestination}
              onModuleChange={(value) => onFieldChange('timeoutDestinationModule', value || '')}
              onDestinationChange={(value) => onFieldChange('timeoutDestinationDestination', value || '')}
              modulePlaceholder={t('queues.form.fields.timeoutDestination.modulePlaceholder')}
              destinationPlaceholder={t('queues.form.fields.timeoutDestination.destinationPlaceholder')}
            />
          </div>
        </div>
      </FormBox>
    </>
  );
}

